import React, {useMemo} from "react";
import {Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {formatNumber, formatPercent} from "../Statistics/format.js";
import {BEREICHE, bereichFarbe, bereichLabel} from "./format.js";
import useStatisticsData from "../Statistics/useStatisticsData.js";
import NutzungWidget from "./NutzungWidget.jsx";

// Deckelt die Anzahl gleichzeitig dargestellter Balken nach unten offen,
// aber gegen einen Ausreißer abgesichert - bei den derzeit rund 28
// Mitarbeitern greift das nie, es ist nur eine Notbremse, falls die
// Belegschaft deutlich wächst.
const MAX_MITARBEITER = 30;

/**
 * "Woran heute gearbeitet wurde": je Mitarbeiter ein gestapelter Balken über
 * die Bereiche - so sieht man auf einen Blick, wer sich womit beschäftigt.
 *
 * BEHOBENER MANGEL (Abgleich Neubau der Darstellung, 19.08.2026): Diese
 * Komponente rief bislang /nutzung/daten/bereiche EINMAL JE MITARBEITER auf
 * (Promise.all über eine per-Mitarbeiter-Schleife) - genau die "Notlösung",
 * die der parallel entstandene Matrix-Endpunkt /nutzung/daten/mitarbeiter-
 * bereiche laut Spezifikation (nutzung-spec2 Abschnitt 3+4, "KEINE Abfrage
 * je Mitarbeiter in einer Schleife") ablösen sollte. Der alte Kommentar
 * hier behauptete noch, es gebe "(noch) keinen" Matrix-Endpunkt - der
 * Endpunkt existierte zum Zeitpunkt des Abgleichs bereits (siehe
 * ActivityQuery::mitarbeiterBereiche() sowie die bereits darauf umgestellte
 * NutzungMitarbeiterTabelle.jsx), er wurde hier nur übersehen. Jetzt EINE
 * Abfrage über alle Mitarbeiter, keine Schleife mehr.
 */
export default function NutzungMitarbeiterBereiche({filters}) {
    const {data, loading, error} = useStatisticsData("/nutzung/daten/mitarbeiter-bereiche", filters);

    const rows = useMemo(() => {
        const personen = Array.isArray(data) ? data : [];
        return personen
            .map((person) => {
                const zeile = {user_id: person.user_id, name: person.name || `Nutzer ${person.user_id}`, __gesamt: 0};
                (person.bereiche || []).forEach((eintrag) => {
                    zeile[eintrag.bereich] = eintrag.ereignisse || 0;
                    zeile[`${eintrag.bereich}__anteil`] = eintrag.anteil || 0;
                    zeile.__gesamt += eintrag.ereignisse || 0;
                });
                return zeile;
            })
            .sort((a, b) => b.__gesamt - a.__gesamt)
            .slice(0, MAX_MITARBEITER);
    }, [data]);

    // Nur Bereiche als Balkensegment anlegen, die bei mindestens einem
    // Mitarbeiter wirklich vorkommen - sonst wäre die Legende voller
    // Bereiche, die im Zeitraum niemand angefasst hat.
    const bereicheVorhanden = useMemo(() => {
        const vorhanden = new Set();
        rows.forEach((zeile) => {
            BEREICHE.forEach((eintrag) => {
                if ((zeile[eintrag.value] || 0) > 0) {
                    vorhanden.add(eintrag.value);
                }
            });
        });
        return BEREICHE.filter((eintrag) => vorhanden.has(eintrag.value));
    }, [rows]);

    const height = Math.max(220, rows.length * 34);

    return (
        <NutzungWidget
            title="Woran heute gearbeitet wurde"
            help="Verteilung der Ereignisse je Mitarbeiter über die Bereiche im gewählten Zeitraum. Beim Überfahren eines Segments erscheinen Bereich, Anteil und Anzahl."
            loading={loading}
            error={error}
            empty={!loading && !error && (rows.length === 0 || bereicheVorhanden.length === 0)}
            height={height}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={rows} layout="vertical" margin={{top: 4, right: 16, left: 8, bottom: 4}}>
                    <XAxis type="number" tickFormatter={formatNumber} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={140} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <Tooltip
                        shared={false}
                        formatter={(value, name, item) => [
                            `${formatNumber(value)} (${formatPercent((item?.payload?.[`${name}__anteil`] || 0) * 100)})`,
                            bereichLabel(name),
                        ]}
                    />
                    <Legend formatter={(value) => bereichLabel(value)} wrapperStyle={{fontSize: 12}} />
                    {bereicheVorhanden.map((eintrag) => (
                        <Bar
                            key={eintrag.value}
                            dataKey={eintrag.value}
                            name={eintrag.value}
                            stackId="bereiche"
                            fill={bereichFarbe(eintrag.value)}
                            barSize={18}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </NutzungWidget>
    );
}
